import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { logEvent } from 'firebase/analytics';

import { ColorScheme, useBackgroundContext } from '../contexts/BackgroundContext';
import BasePageHeader, { UPageType } from './BasePageHeader';
import { BasePageContent } from './BasePage';
import SparkleButton from '../components/buttons/SparkleButton';
import { useEditEventContext } from '../contexts/EditEventContext';
import PhotoCollage from '../assets/promos/photoCollage.png';
import { InspirationCategory, InspirationIdea, getInspirationsByType } from '../lib/inspiration';
import { DesktopShow } from '../components/styled/Common';
import { FilterPill } from '../components/PillToggle';
import { getUpcoming } from '../components/seasonal/sampleEvents';
import SeasonalInvite from '../components/seasonal/SeasonalInvite';
import AppStoreDownload from '../components/icons/AppStoreDownload';
import GooglePlayStoreDownload from '../components/icons/GooglePlayStoreDownload';
import OrangeGradientBackgroundDesktop from '../components/backgrounds/OrangeGradientBackgroundDesktop.svg';
import OrangeGradientBackgroundMobile from '../components/backgrounds/OrangeGradientBackgroundMobile.svg';
import { firebaseAnalytics } from '../core/libs/Firebase';
import { useUserContext } from '../contexts/UserContext';
import PillRow from './Welcome/PillRow';
import WelcomeFooter from './Welcome/WelcomeFooter';

import { DeviceQueries } from 'Common/src/components/styled';
import Slideshow from '../components/Slideshow';
import { logSumoEvent, ULogApplication, ULogSeverity, ULogTag } from 'Common/src/api/SumoLogicApi';

const WelcomePage = () => {
  const navigate = useNavigate();
  const userContext = useUserContext();
  const backgroundContext = useBackgroundContext();
  const editEventContext = useEditEventContext();
  const [inspirationsByType, setInspirationsByType] = useState(new Map<InspirationCategory, InspirationIdea[]>());

  useEffect(() => {
    document.title = 'Shine';
    logEvent(firebaseAnalytics, 'page_view', {
      page_title: 'Welcome - Analytics'
    });
    backgroundContext.setScheme({ scheme: ColorScheme.Welcome });

    void fetchInspirations();
  }, []);

  const fetchInspirations = async () => {
    setInspirationsByType(
      await getInspirationsByType()
    );
  };

  const onCreateClick = () => {
    void logSumoEvent({
      app: ULogApplication.ELK,
      severity: ULogSeverity.INFO,
      userId: userContext.id,
      tag: ULogTag.UserAction,
      message: '[WelcomePage] Clicked on create invite button'
    });

    editEventContext.clearEvent();
    navigate('/instant');
  };

  const viewGallery = (category?: InspirationCategory) => {
    void logSumoEvent({
      app: ULogApplication.ELK,
      severity: ULogSeverity.INFO,
      userId: userContext.id,
      tag: ULogTag.UserAction,
      message: '[WelcomePage] Clicked on view gallery pill'
    });

    navigate('/instant', {
      state: {
        category
      }
    });
  }

  const inspire = (inspiration: InspirationIdea) => {
    void logSumoEvent({
      app: ULogApplication.ELK,
      severity: ULogSeverity.INFO,
      userId: userContext.id,
      tag: ULogTag.UserAction,
      message: '[WelcomePage] Clicked on inspo gallery event'
    });

    navigate(`/event/create/fromGallery/${inspiration.id}`);
  };

  const handleDownloadShineiOSClick = () => {
    window.open('https://apps.apple.com/us/app/sunshine-photos/id6469663904?itsct=apps_box_badge&amp;itscg=30200',
      '_blank');
  };

  const handleDownloadShineAndroidClick = () => {
    window.open('https://play.google.com/store/apps/details?id=com.lumilabs.android.shine.ga', '_blank');
  };

  const slideshowEvents = getUpcoming().map(event => <SeasonalInvite key={event.event.id} event={event.event}/>);
  const seasonalInspoGalleryCategories = [];
  const generalInspoGalleryCategories = [InspirationCategory.BD, InspirationCategory.EN, InspirationCategory.HO];

  const now = new Date();
  if (now.getMonth() === 10 || now.getMonth() === 11) {
    seasonalInspoGalleryCategories.unshift(InspirationCategory.HO);
  }

  const photosAppPromo = <>
    <GradientBackground/>
    <AppPromoContainer>
      <PromoContent>
        <PromoText>
          <PromoTitle>Easy photo sharing<DesktopShow><br/></DesktopShow> for your event</PromoTitle>
          <PromoSubtitle>Try the Shine photo sharing app</PromoSubtitle>
        </PromoText>
        <ButtonRow>
          <AppLinkContainer onClick={handleDownloadShineiOSClick}>
            <AppStoreDownload/>
          </AppLinkContainer>
          <AppLinkContainer onClick={handleDownloadShineAndroidClick}>
            <GooglePlayStoreDownload/>
          </AppLinkContainer>
        </ButtonRow>
      </PromoContent>
      <AppPromoImage src={PhotoCollage}/>
    </AppPromoContainer>
  </>

  const slideshowData = [...slideshowEvents, photosAppPromo];

  return <>
    <BasePageHeader page={UPageType.Welcome}/>
    <BasePageContent $wide={true} $flush={true} $full={true}>
      <ContentContainer>
        <HeadlineRow>
          <HeadlineTextContainer>
            <HeadlineMainText>Bright shiny events</HeadlineMainText>
            <ValuePropText>Unique invites with generative AI, Integrated easy photo sharing</ValuePropText>
          </HeadlineTextContainer>
          <SparkleButtonContainer>
            <SparkleButton buttonText="Create your invite" disabled={false} onClick={onCreateClick}
                           buttonColor={'#000000'} textColor={'#FFFFFF'}/>
          </SparkleButtonContainer>
        </HeadlineRow>
        <PillRow onPillClick={viewGallery}/>
        <Slideshow slidesData={slideshowData}/>
        {/*<Row>*/}
        {/*  {photosAppPromo}*/}
        {/*</Row>*/}
        <InspirationGalleryContainer>
          <InspirationGallerySectionContainer>
            <InspirationCategorySections>
              {seasonalInspoGalleryCategories.map(category => <CategoryContainer key={category}>
                <div>
                  <CategoryHeader>
                    <CategoryTitle>{category}</CategoryTitle>
                    <ViewGalleryPillButton onClick={() => viewGallery(category)}>View gallery</ViewGalleryPillButton>
                  </CategoryHeader>
                </div>
                <CarouselWrapper>
                  <GalleryContainer>
                    {
                      inspirationsByType.get(category)
                        ?.sort((a, b) => (a.date?.getTime() ?? 0) - (b.date?.getTime() ?? 0))
                        .map((inspiration) => <Inspiration
                          key={inspiration.id} onClick={() => inspire(inspiration)}>
                          <InspirationImage src={inspiration.photoUrl}/>
                          <InspirationText>
                            {inspiration.title}
                          </InspirationText>
                        </Inspiration>)
                    }
                  </GalleryContainer>
                </CarouselWrapper>
              </CategoryContainer>)}
            </InspirationCategorySections>
          </InspirationGallerySectionContainer>
          <InspirationGallerySectionContainer>
            <InspirationCategorySections>
              {generalInspoGalleryCategories.map(category => <CategoryContainer key={category}>
                <CategoryHeader>
                  <CategoryTitle>{category}</CategoryTitle>
                  <ViewGalleryPillButton onClick={() => viewGallery(category)}>View gallery</ViewGalleryPillButton>
                </CategoryHeader>
                <CarouselWrapper>
                  <GalleryContainer>
                    {
                      inspirationsByType.get(category)?.map((inspiration) => <Inspiration
                        key={inspiration.id} onClick={() => inspire(inspiration)}>
                        <InspirationImage src={inspiration.photoUrl}/>
                        <InspirationText>
                          {inspiration.title}
                        </InspirationText>
                      </Inspiration>)
                    }
                  </GalleryContainer>
                </CarouselWrapper>
              </CategoryContainer>)}
            </InspirationCategorySections>
          </InspirationGallerySectionContainer>
        </InspirationGalleryContainer>
      </ContentContainer>
      <WelcomeFooter/>
    </BasePageContent>
  </>;
};

const ContentContainer = styled.div`
  display: flex;
  gap: 20px;
  flex-wrap: wrap;
  width: 100svw;
  padding: 0 40px 45px 40px;
  box-sizing: border-box;

  @media (${DeviceQueries.mobile}) {
    padding: 0 16px 50px 16px;
  }
`;


const Row = styled.div`
  display: flex;
  gap: 25px;
  width: 100%;

  @media (${DeviceQueries.mobile}) {
    flex-direction: column;
    gap: 0;
  }
`

const HeadlineRow = styled(Row)`
  align-items: center;
  justify-content: space-between;

  @media (${DeviceQueries.mobile}) {
    align-items: flex-start;
  }
`

const HeadlineTextContainer = styled.div`
  display: flex;
  flex-direction: column;

  @media (${DeviceQueries.mobile}) {
    gap: 8px;
  }
`

const HeadlineMainText = styled.div`
  color: #000;
  font-size: 70px;
  font-weight: 500;
  line-height: 80px;
  margin-left: -3px;

  @media (${DeviceQueries.mobile}) {
    font-size: 45px;
    line-height: 50px;
    margin: 0;
  }
`

const ValuePropText = styled.div`
  color: #000;
  font-size: 24px;

  @media (${DeviceQueries.mobile}) {
    font-size: 16px;
  }
`

const SparkleButtonContainer = styled.span`
  --button-text-color: #FF6422;
  display: inline-block;
  margin: 10px 0;
`;

const AppPromoContainer = styled.div`
  position: relative;
  display: flex;
  //border: 1px solid #E5E5E5;
  //padding: 30px 40px;
  gap: 30px;
  overflow: hidden;
  align-items: center;
  width: 100%;
  height: 100%;
  justify-content: space-between;
    // background-image: url(${OrangeGradientBackgroundDesktop});
  // background-size: cover;
  // background-repeat: no-repeat;
  // background-position: center;

  @media (${DeviceQueries.mobile}) {
      //background-image: url(${OrangeGradientBackgroundMobile});
    flex-direction: column-reverse;
    padding: 0;
    justify-content: center;
  }
`

const GradientBackground = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-size: cover;
  z-index: 0; // Below the content
  border: 1px solid #E5E5E5;
  overflow: hidden;
  background-image: url(${OrangeGradientBackgroundDesktop});
  background-repeat: no-repeat;
  background-position: center;

  @media (${DeviceQueries.mobile}) {
    background-image: url(${OrangeGradientBackgroundMobile});
  }
`

const PromoContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 45px;
  overflow: hidden;
  z-index: 1;

  @media (${DeviceQueries.mobile}) {
    padding: 0 20px;
    gap: 10px;
  }
`

const PromoText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 35px;

  @media (${DeviceQueries.mobile}) {
    gap: 10px;
  }
`

const PromoTitle = styled.div`
  color: #000;
  font-size: 54px;
  font-weight: 600;
  line-height: 66px;

  @media (${DeviceQueries.mobile}) {
    font-size: 32px;
    letter-spacing: -0.96px;
    line-height: 38px;
  }
`

const PromoSubtitle = styled.div`
  color: #000;
  font-size: 40px;
  font-weight: 400;
  line-height: 50px;

  @media (${DeviceQueries.mobile}) {
    font-size: 20px;
    line-height: 32px; /* 160% */
  }
`

const ButtonRow = styled(Row)`
  gap: 0 25px;
  flex-wrap: wrap;

  @media (${DeviceQueries.mobile}) {
    align-items: center;
  }
`

const GalleryContainer = styled.div`
  display: flex;
  overflow-x: scroll;
  overflow-y: hidden;
  white-space: nowrap;
  margin-bottom: -17px; // Hide bottom scroll bar
  padding-bottom: 17px; // Prevent bottom content cut off
  gap: 41px;

  @media (${DeviceQueries.mobile}) {
    gap: 16px;
  }
`;

const CategoryContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  width: 100%;

  @media (${DeviceQueries.mobile}) {
    gap: 12px;
  }
`;

const Inspiration = styled.div`
  width: 311px;
  margin: 0;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  gap: 8px;

  @media (${DeviceQueries.mobile}) {
    width: 242px;
  }
`;

const InspirationImage = styled.img`
  width: 311px;
  max-height: 311px;
  object-fit: contain;
  vertical-align: top;
  border-radius: 10px;
  opacity: 1;

  @media (${DeviceQueries.mobile}) {
    width: 242px;
    max-height: 242px;
  }
`;

const InspirationText = styled.div`
  display: flex;
  flex-direction: column;
  transition: background-color 1s;
  flex: 1;
  font-size: 16px;
  font-weight: 500;
`;

const CategoryHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
`

const CategoryTitle = styled.div`
  font-size: 40px;
  font-weight: 500;

  @media (${DeviceQueries.mobile}) {
    font-size: 24px;
  }
`;

const CategorySubtitle = styled.div`
  font-size: 18px;
  font-weight: 400;

  @media (${DeviceQueries.mobile}) {
    font-size: 12px;
  }
`

const CarouselWrapper = styled.div`
  overflow: hidden;
`;

const InspirationGalleryContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 40px;
  overflow: hidden;

  @media (${DeviceQueries.mobile}) {
    gap: 20px;
  }
`

const InspirationGallerySectionContainer = styled.div`
  width: 100%;
`

const InspirationCategorySections = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 40px;

  @media (${DeviceQueries.mobile}) {
    gap: 20px;
  }
`

const ViewGalleryPillButton = styled(FilterPill).attrs({
  as: 'button'
})`
  width: fit-content;
  font-family: Poppins, sans-serif;
  color: #000000;

  &:hover {
    filter: brightness(90%);
  }
`

const AppLinkContainer = styled.div`
  cursor: pointer;
`

const AppPromoImage = styled.img`
  width: 38%;
  max-width: 500px;
  height: fit-content;

  @media (${DeviceQueries.mobile}) {
    width: 100%;
  }
`

export default WelcomePage;

